// Validation utility functions

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FieldErrors {
  [key: string]: string;
}

// Email validation
export const validateEmail = (email: string): ValidationResult => {
  if (!email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  if (email.length > 255) {
    return { isValid: false, error: 'Email must be less than 255 characters' };
  }
  return { isValid: true };
};

// Password validation
export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters' };
  }
  return { isValid: true };
};

// Required field validation
export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  if (!value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true };
};

// Translation key validation (dot notation)
export const validateTranslationKey = (key: string): ValidationResult => {
  if (!key.trim()) {
    return { isValid: false, error: 'Key is required' };
  }
  // Allow alphanumeric, dots, underscores, and hyphens
  const keyRegex = /^[a-zA-Z][a-zA-Z0-9._-]*$/;
  if (!keyRegex.test(key)) {
    return { isValid: false, error: 'Key must start with a letter and contain only letters, numbers, dots, underscores, or hyphens' };
  }
  if (key.length > 255) {
    return { isValid: false, error: 'Key must be less than 255 characters' };
  }
  return { isValid: true };
};

// Translation value validation
export const validateTranslationValue = (value: string): ValidationResult => {
  if (!value.trim()) {
    return { isValid: false, error: 'Value is required' };
  }
  if (value.length > 5000) {
    return { isValid: false, error: 'Value must be less than 5000 characters' };
  }
  return { isValid: true };
};

// Screen name validation
export const validateScreenName = (screen: string): ValidationResult => {
  if (!screen.trim()) {
    return { isValid: true }; // Optional field
  }
  const screenRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
  if (!screenRegex.test(screen)) {
    return { isValid: false, error: 'Screen must start with a letter and contain only letters, numbers, or underscores' };
  }
  if (screen.length > 100) {
    return { isValid: false, error: 'Screen must be less than 100 characters' };
  }
  return { isValid: true };
};

// Component name validation (PascalCase)
export const validateComponentName = (component: string): ValidationResult => {
  if (!component.trim()) {
    return { isValid: true }; // Optional field
  }
  const componentRegex = /^[A-Z][a-zA-Z0-9]*$/;
  if (!componentRegex.test(component)) {
    return { isValid: false, error: 'Component must be in PascalCase (e.g., LoginScreen)' };
  }
  if (component.length > 100) {
    return { isValid: false, error: 'Component must be less than 100 characters' };
  }
  return { isValid: true };
};

// Description validation
export const validateDescription = (description: string): ValidationResult => {
  if (description.length > 500) {
    return { isValid: false, error: 'Description must be less than 500 characters' };
  }
  return { isValid: true };
};

// File validation
export const validateFile = (file: File, options?: {
  maxSize?: number; // in bytes
  allowedTypes?: string[];
}): ValidationResult => {
  const maxSize = options?.maxSize || 50 * 1024 * 1024; // 50MB default
  const allowedTypes = options?.allowedTypes;

  if (file.size > maxSize) {
    const sizeMB = Math.round(maxSize / (1024 * 1024));
    return { isValid: false, error: `File size must be less than ${sizeMB}MB` };
  }

  if (allowedTypes && allowedTypes.length > 0) {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !allowedTypes.includes(fileExt)) {
      return { isValid: false, error: `File type must be one of: ${allowedTypes.join(', ')}` };
    }
  }

  // Validate filename
  const filenameRegex = /^[a-zA-Z0-9._-]+$/;
  if (!filenameRegex.test(file.name)) {
    return { isValid: false, error: 'Filename can only contain letters, numbers, dots, underscores, or hyphens' };
  }

  return { isValid: true };
};

// Search query validation
export const validateSearchQuery = (query: string): ValidationResult => {
  if (query.length > 255) {
    return { isValid: false, error: 'Search query must be less than 255 characters' };
  }
  return { isValid: true };
};

// Helper to check if all fields are valid
export const isFormValid = (errors: FieldErrors): boolean => {
  return Object.values(errors).every(error => !error);
};
